import { Check, ChevronDown, EyeOff, MessageChatCircle } from "@untitledui/icons";
import { useState } from "react";
import { cx } from "@/utils/cx";
import type { RiskState } from "./RiskRow";
import { RiskRow } from "./RiskRow";
import { findGroupIdForSelection, GROUP_ICONS, type RiskGroup, type RiskItem } from "./risk-groups";

type RiskGroupAccordionProps = {
  groups: RiskGroup[];
  getRiskState: (item: RiskItem) => RiskState;
  // Only the merchant-facing Configure Safety page syncs the open group(s) to the URL —
  // callers that don't care (e.g. the workspace template's read-only tab) can omit these
  // and get plain local expand/collapse.
  selectedRiskIds?: string[];
  onSelectRisk?: (id: string) => void;
  onRemoveRisks?: (ids: string[]) => void;
  // Merchant Configure Safety reads these rows as the merchant's live risks
  // ("Active"); the workspace defaults template reads them as what new merchants
  // inherit ("on by default"). Defaults to the merchant wording.
  wording?: "active" | "defaults";
};

export const RiskGroupAccordion = ({
  groups,
  getRiskState,
  selectedRiskIds,
  onSelectRisk = () => {},
  onRemoveRisks = () => {},
  wording = "active",
}: RiskGroupAccordionProps) => {
  const [open, setOpen] = useState<Set<string>>(new Set());

  // Each selected id (a specific risk's id from a row click, or a group's own id from a
  // header click, see toggleGroup) resolves to the group that must open for it.
  const selectedGroupIds = new Set(
    (selectedRiskIds ?? []).map((id) => findGroupIdForSelection(groups, id)).filter((id): id is string => !!id),
  );

  // Opening a group via its header selects the group itself (not an arbitrary risk from
  // it), so that selection — and therefore this group being open — survives a
  // refresh/shared link, same as clicking an individual row. Closing an open group
  // drops every selected id that belongs to it (its own id and/or any of its risks');
  // other manually opened groups are pure local state, not tracked in the URL.
  const toggleGroup = (group: RiskGroup) => {
    const isCurrentlyOpen = open.has(group.id) || selectedGroupIds.has(group.id);
    setOpen((prev) => {
      const next = new Set(prev);
      if (isCurrentlyOpen) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
    if (isCurrentlyOpen) {
      const idsToRemove = (selectedRiskIds ?? []).filter((id) => findGroupIdForSelection(groups, id) === group.id);
      if (idsToRemove.length > 0) onRemoveRisks(idsToRemove);
    } else {
      onSelectRisk(group.id);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const isOpen = open.has(group.id) || selectedGroupIds.has(group.id);
        const active = group.items.filter((i) => getRiskState(i).enabled).length;
        const total = group.items.length;
        const someActive = active > 0;
        const Icon = GROUP_ICONS[group.id] ?? MessageChatCircle;

        return (
          <div key={group.id} className="rounded-md border border-border-primary bg-white overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-unbox-cream-50 transition-colors"
              onClick={() => toggleGroup(group)}
            >
              <Icon aria-hidden="true" className="w-5 h-5 text-secondary shrink-0" />
              <span className="flex-1 text-sm font-medium text-primary">{group.name}</span>
              {someActive ? (
                <span className="text-sm font-medium flex items-center gap-2 text-success-700">
                  {wording === "defaults"
                    ? `${active} of ${total} on by default`
                    : `${active} of ${total} Risks Active`}
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-success-50 shrink-0">
                    <Check aria-hidden="true" className="w-3.5 h-3.5 text-success-600" />
                  </span>
                </span>
              ) : (
                <span className="text-sm font-medium flex items-center align-center gap-2 text-tertiary">
                  {wording === "defaults" ? `${total} off by default` : `${total} Risks Disabled`}
                  <EyeOff aria-hidden="true" className="w-[20px] h-[20px] text-quaternary shrink-0" />
                </span>
              )}
              <ChevronDown
                aria-hidden="true"
                className={cx(
                  "w-6 h-6 text-quaternary transition-transform duration-150 shrink-0",
                  isOpen && "rotate-180",
                )}
              />
            </button>

            {isOpen && (
              <div>
                {group.items.map((item) => (
                  <RiskRow
                    key={item.key}
                    item={item}
                    state={getRiskState(item)}
                    onClick={() => onSelectRisk(item.key)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
